import {
  Button,
  Grid,
  Stack,
  Typography,
  Link,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import React from "react";
import { CustomTable, InputControl, PageListLayout } from "../../../shared";
import { useNavigate, useParams } from "react-router-dom";
import { useRolesAndPermissions } from "../hooks";
import { createColumnHelper } from "@tanstack/react-table";
import { ChevronRight } from "@mui/icons-material";

export const AddorEditRoles = () => {
  const { id } = useParams();
  const {
    rolesAndPermissions,
    handleOnChange,
    validator,
    handleCheckPermission,
    handleCheckAllPermissions,
    onSubmit,
    handleChecked,
    handleIndeterminate,
    createRoles,
    editRoles,
  } = useRolesAndPermissions({
    isPermissionList: true,
    isList: false,
    roleId: id,
  });
  const navigate = useNavigate();

  const breadcrumbs = [
    <Link
      underline="none"
      key="1"
      color="#1760D2"
      onClick={(e) => navigate("/roles-and-permissions")}
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      Roles & Permissions
    </Link>,
    <Typography
      key="2"
      color="inherit"
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      {id ? "Edit" : "Add New Role"}
    </Typography>,
  ];
  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.display({
      id: "index",
      meta: { align: "center", width: "20px", padding: 0, paddingLeft: "4px" },
      cell: (props) => {
        const { row } = props;
        return (
          !row.depth && (
            <IconButton
              onClick={() => row.toggleExpanded()}
              size="small"
              sx={{
                rotate: row.getIsExpanded() ? "90deg" : "0deg",
                transition: "0.3s all",
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          )
        );
      },
    }),
    columnHelper.accessor("name", {
      header: "Permission Name",
      cell: (props) => {
        const { row } = props;
        return !row.depth ? (
          <Typography
            fontWeight={500}
            fontSize="14px"
            onClick={() => row.toggleExpanded()}
            sx={{ cursor: "pointer" }}
          >
            {row.original.name}
          </Typography>
        ) : (
          <Typography fontSize="14px" fontWeight={400}>
            {row.original.display_name}
          </Typography>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: () => (
        <FormControlLabel
          sx={{ width: "18px", height: "18px" }}
          control={
            <Checkbox
              checked={handleChecked(2, null, null)}
              indeterminate={handleIndeterminate(2, null)}
              size="small"
              color="primary"
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: 24,
                },
              }}
              onChange={handleCheckAllPermissions}
            />
          }
        />
      ),
      meta: { align: "center", width: "20px" },
      cell: (props) => {
        const { row } = props;
        return (
          <FormControlLabel
            sx={{ width: "18px", height: "18px" }}
            control={
              <Checkbox
                checked={handleChecked(
                  row.depth,
                  row.original.id,
                  row.originalSubRows
                )}
                indeterminate={
                  !row.depth
                    ? handleIndeterminate(row.depth, row.originalSubRows)
                    : false
                }
                size="small"
                color="primary"
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: 24,
                  },
                }}
                onChange={(e) => handleCheckPermission(e, row, "body")}
              />
            }
          />
        );
      },
    }),
  ];

  return (
    <PageListLayout
      breadcrumbs={breadcrumbs}
      headerText={id ? "Edit Role" : "Add New Role"}
      isChanged={false}
      rightContent={
        <Stack direction="row" gap="8px">
          <Button
            type="button"
            size="small"
            variant="contained"
            color="inherit"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="small"
            variant="contained"
            onClick={onSubmit}
            disabled={
              createRoles?.isPending ||
              editRoles?.isPending ||
              !rolesAndPermissions.data.permission_id.length
            }
          >
            {id ? "Update" : "Save"}
          </Button>
        </Stack>
      }
      sx={{ p: "16px", pt: 0, backgroundColor: "#ffffff" }}
    >
      <Stack
        sx={{
          py: "24px",
          borderBottom: "1px solid #DFE8F2",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{ width: "150px" }}
            >
              Basic Details
            </Typography>
          </Grid>
          <Grid item sm={8}>
            <Stack width={"100%"} gap="15px">
              <InputControl
                name="name"
                type="text"
                label="Role Name"
                required
                error={validator.current.message(
                  "Role Name",
                  rolesAndPermissions?.data?.name,
                  "required|min:3"
                )}
                helperText={validator.current.message(
                  "Role Name",
                  rolesAndPermissions?.data?.name,
                  "required|min:3"
                )}
                onChange={(e) => handleOnChange(e)}
                value={rolesAndPermissions?.data?.name}
              />
            </Stack>
          </Grid>
        </Grid>
      </Stack>
      <Stack>
        <CustomTable
          columns={columns}
          defaultData={rolesAndPermissions?.allPermissionsList ?? []}
          isPagination={false}
          showSearchBar={false}
          subRow="permissions"
          manualPagination={true}
          headerText="Permissions"
          tableHeaderStyles={{
            fontSize: "18px",
            px: 0,
            height: "56px",
            borderBottom: 1,
            borderColor: "divider",
            borderRadius: 0,
          }}
          enableTableContainerPaddingBottom
        />
      </Stack>
    </PageListLayout>
  );
};
